import React from 'react';
import type { ComponentType, ReactNode } from 'react';
import { ModuleAccent } from '../../theme/ThemeContext';
import ModuleErrorBoundary from './ModuleErrorBoundary';
import { MODULE_ACCENTS } from '../../utils/constants';
import { brand } from '../../theme/tokens';

import Congregation from '../../features/congregation/Congregation';
import BirthdayAnniversary from '../../features/celebrations/BirthdayAnniversary';
import EventManagement from '../../features/events/EventManagement';
import ChurchCalendar from '../../features/calendar/ChurchCalendar';
import OfferingManagement from '../../features/offerings/OfferingManagement';
import ExpenseManagement from '../../features/expenses/ExpenseManagement';
import SundaySchool from '../../features/sundaySchool/SundaySchool';
import Accounts from '../../features/accounts/Accounts';
import AttendanceManagement from '../../features/attendance/AttendanceManagement';
import PastoralCare from '../../features/pastoralCare/PastoralCare';
import CemeteryManagement from '../../features/cemetery/CemeteryManagement';
import YouthFellowship from '../../features/youth/YouthFellowship';
import ChoirManagement from '../../features/choir/ChoirManagement';
import AssetManagement from '../../features/assets/AssetManagement';
import MeetingManagement from '../../features/meetings/MeetingManagement';
import PastorHistory from '../../features/pastorHistory/PastorHistory';
import CommitteeHistory from '../../features/committee/CommitteeHistory';
import BudgetManagement from '../../features/budget/BudgetManagement';
import FinancialDashboard from '../../features/financial/FinancialDashboard';
import SettingsPage from '../../features/settings/SettingsPage';
import BaptismRegister from '../../features/registers/BaptismRegister';
import MarriageRegister from '../../features/registers/MarriageRegister';
import Missionaries from '../../features/missionaries/Missionaries';
import StatutoryPayments from '../../features/statutory/StatutoryPayments';

/** Modules carrying this accent have no colour of their own. */
const DEFAULT_ACCENT = brand.primary;

// Module id -> component. Replaces the 22-line `{activeModule === 'X' && <X/>}`
// chain that used to sit at the bottom of Dashboard.jsx. Keys must match
// MODULES[].id in utils/constants.js.
/** Every module screen takes the same single prop. */
export type ModuleComponent = ComponentType<{ isDarkMode?: boolean }>;

export const MODULE_COMPONENTS: Record<string, ModuleComponent> = {
  'Members & Families': Congregation,
  'Birthday & Anniversary': BirthdayAnniversary,
  'Event Management': EventManagement,
  'Church Calendar': ChurchCalendar,
  'Offerings': OfferingManagement,
  'Expenses': ExpenseManagement,
  'Sunday School': SundaySchool,
  'Accounts': Accounts,
  'Attendance': AttendanceManagement,
  'Pastoral Care': PastoralCare,
  'Cemetery': CemeteryManagement,
  'Youth Fellowship': YouthFellowship,
  'Choir Management': ChoirManagement,
  'Asset Management': AssetManagement,
  'Meeting Management': MeetingManagement,
  'Pastor Turnover': PastorHistory,
  'Committee History': CommitteeHistory,
  'Budget Management': BudgetManagement,
  'Financial Analytics': FinancialDashboard,
  'Baptism': BaptismRegister,
  'Marriage': MarriageRegister,
  'Missionaries': Missionaries,
  'Statutory Payments': StatutoryPayments,
  'Settings': SettingsPage
};

export interface ModuleRouterProps {
  moduleId: string;
  isDarkMode?: boolean;
  /** Rendered when the id matches no module. */
  fallback?: ReactNode;
}

const ModuleRouter = ({ moduleId, isDarkMode, fallback = null }: ModuleRouterProps) => {
  const Module = MODULE_COMPONENTS[moduleId];
  if (!Module) return <>{fallback}</>;

  // Every UI-kit component rendered inside picks up the module's accent.
  // A module whose accent IS the default purple has no identity colour of its
  // own, so it is left unset and inherits the parish brand colour instead.
  const accent = MODULE_ACCENTS[moduleId];
  return (
    <ModuleAccent accent={accent === DEFAULT_ACCENT ? undefined : accent}>
      {/* Keyed by module so each screen mounts fresh: a module that crashed
          must not leave its wreckage behind when the menu moves on. */}
      <ModuleErrorBoundary key={moduleId} moduleId={moduleId} isDarkMode={isDarkMode}>
        <Module isDarkMode={isDarkMode} />
      </ModuleErrorBoundary>
    </ModuleAccent>
  );
};

export default ModuleRouter;
